import React, { useEffect, useState, createContext, useContext, useCallback } from 'react';
import { getUserSettings, updateUserSettings, updateUserLastActive } from '../services/firebase';
import { auth } from '../config/firebase';
import { useNotifications } from './NotificationContext';

export type AppMode = 'dating' | 'hookup';

interface ModePreferences {
  dating: {
    bio: string;
    lookingFor: string[];
    ageRange: [number, number];
    showSaferOptions: boolean;
    enableHealthTracking: boolean;
  };
  hookup: {
    bio: string;
    lookingFor: string[];
    interests: string[];
    showLocation: boolean;
    anonymousMode: boolean;
  };
}

interface AppSettings {
  useMetric: boolean;
  currentMode: AppMode;
  modePreferences: ModePreferences;
  hasCompletedModeSetup: boolean;
  autoSwitchByTime: boolean;
  timeBasedSwitching: {
    datingStart: string; // "09:00"
    datingEnd: string;   // "21:00"
  };
  privacySettings: {
    showModeInProfile: boolean;
    allowCrossModeMatching: boolean;
  };
}

type AppContextType = {
  // Loading state
  isLoading: boolean;
  
  // Basic settings
  useMetric: boolean;
  setUseMetric: (value: boolean) => void;
  
  // Mode management
  currentMode: AppMode;
  switchMode: (mode: AppMode, showNotification?: boolean) => Promise<void>;
  switchModeWithTransition: (mode: AppMode) => Promise<void>;
  
  // Mode preferences
  modePreferences: ModePreferences;
  updateModePreferences: (mode: AppMode, preferences: Partial<ModePreferences[AppMode]>) => Promise<void>;
  
  // Setup and configuration
  hasCompletedModeSetup: boolean;
  completeModeSetup: () => Promise<void>;
  
  // Auto-switching
  autoSwitchByTime: boolean;
  setAutoSwitchByTime: (enabled: boolean) => Promise<void>;
  timeBasedSwitching: AppSettings['timeBasedSwitching'];
  updateTimeBasedSwitching: (settings: AppSettings['timeBasedSwitching']) => Promise<void>;
  
  // Privacy
  privacySettings: AppSettings['privacySettings'];
  updatePrivacySettings: (settings: Partial<AppSettings['privacySettings']>) => Promise<void>;
  
  // Utilities
  getModeDisplayName: (mode: AppMode) => string;
  getModeDescription: (mode: AppMode) => string;
  getModeIcon: (mode: AppMode) => string;
  
  // Analytics
  logModeSwitch: (fromMode: AppMode, toMode: AppMode, method: 'manual' | 'auto' | 'scheduled') => void;
};

const DEFAULT_MODE_PREFERENCES: ModePreferences = {
  dating: {
    bio: '',
    lookingFor: ['dating', 'relationship'],
    ageRange: [22, 35],
    showSaferOptions: true,
    enableHealthTracking: true
  },
  hookup: {
    bio: '',
    lookingFor: ['casual', 'hookup'],
    interests: [],
    showLocation: false,
    anonymousMode: false
  }
};

const DEFAULT_SETTINGS: AppSettings = {
  useMetric: true,
  currentMode: 'dating',
  modePreferences: DEFAULT_MODE_PREFERENCES,
  hasCompletedModeSetup: false,
  autoSwitchByTime: false,
  timeBasedSwitching: {
    datingStart: '09:00',
    datingEnd: '21:00'
  },
  privacySettings: {
    showModeInProfile: true,
    allowCrossModeMatching: false
  }
};

// Create context
const EnhancedAppContext = createContext<AppContextType | null>(null);

// Custom hook
export const useEnhancedApp = () => {
  const context = useContext(EnhancedAppContext);
  if (!context) {
    throw new Error('useEnhancedApp must be used within an EnhancedAppProvider');
  }
  return context;
};

// Provider component
export const EnhancedAppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [settings, setSettings] = useState<AppSettings>(DEFAULT_SETTINGS);
  const { showSuccess, showInfo } = useNotifications();

  // Load settings from Firebase/localStorage
  const loadSettings = useCallback(async () => {
    try {
      const currentUser = auth?.currentUser;
      if (currentUser) {
        try {
          const userSettings = await getUserSettings(currentUser.uid);
          if (userSettings) {
            setSettings(prev => ({
              ...prev,
              ...userSettings,
              modePreferences: {
                ...DEFAULT_MODE_PREFERENCES,
                ...userSettings.modePreferences
              }
            }));
          }
          await updateUserLastActive(currentUser.uid);
        } catch (error) {
          console.error('Error loading settings from Firebase:', error);
          fallbackToLocalStorage();
        }
      } else {
        fallbackToLocalStorage();
      }
    } catch (error) {
      console.error('Error in loadSettings:', error);
      fallbackToLocalStorage();
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fallbackToLocalStorage = () => {
    try {
      const savedSettings = localStorage.getItem('appSettings');
      if (savedSettings) {
        const parsed = JSON.parse(savedSettings);
        setSettings(prev => ({ ...prev, ...parsed }));
      }
    } catch (error) {
      console.error('Error loading from localStorage:', error);
    }
  };

  // Save settings to Firebase/localStorage
  const saveSettings = useCallback(async (newSettings: Partial<AppSettings>) => {
    const updatedSettings = { ...settings, ...newSettings };
    setSettings(updatedSettings);

    const currentUser = auth?.currentUser;
    if (currentUser) {
      try {
        await updateUserSettings(currentUser.uid, updatedSettings);
      } catch (error) {
        console.error('Error saving to Firebase:', error);
        localStorage.setItem('appSettings', JSON.stringify(updatedSettings));
      }
    } else {
      localStorage.setItem('appSettings', JSON.stringify(updatedSettings));
    }
  }, [settings]);

  // Initialize settings
  useEffect(() => {
    loadSettings();

    // Auth state listener
    if (auth) {
      const unsubscribe = auth.onAuthStateChanged(() => {
        loadSettings();
      });
      return unsubscribe;
    }
  }, [loadSettings]);

  // Auto mode switching based on time
  useEffect(() => {
    if (!settings.autoSwitchByTime) return;

    const checkTimeBasedSwitch = () => {
      const now = new Date();
      const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
      
      const isWithinDatingHours = currentTime >= settings.timeBasedSwitching.datingStart && 
                                  currentTime <= settings.timeBasedSwitching.datingEnd;
      
      const shouldBeDating = isWithinDatingHours;
      const shouldBeHookup = !isWithinDatingHours;
      
      if (shouldBeDating && settings.currentMode === 'hookup') {
        switchMode('dating', false).then(() => {
          showInfo('Switched to Dating Mode', 'Based on your time preferences');
          logModeSwitch('hookup', 'dating', 'scheduled');
        });
      } else if (shouldBeHookup && settings.currentMode === 'dating') {
        switchMode('hookup', false).then(() => {
          showInfo('Switched to Hookup Mode', 'Based on your time preferences');
          logModeSwitch('dating', 'hookup', 'scheduled');
        });
      }
    };

    // Check every minute
    const interval = setInterval(checkTimeBasedSwitch, 60000);
    
    // Check immediately
    checkTimeBasedSwitch();
    
    return () => clearInterval(interval);
  }, [settings.autoSwitchByTime, settings.currentMode, settings.timeBasedSwitching]);

  // Mode switching functions
  const switchMode = useCallback(async (mode: AppMode, showNotification = true) => {
    const previousMode = settings.currentMode;
    
    await saveSettings({ currentMode: mode });
    
    if (showNotification) {
      showSuccess(
        `Switched to ${getModeDisplayName(mode)} Mode`,
        getModeDescription(mode)
      );
    }
    
    logModeSwitch(previousMode, mode, 'manual');
  }, [settings.currentMode, saveSettings, showSuccess]);

  const switchModeWithTransition = useCallback(async (mode: AppMode) => {
    // Add a smooth transition effect
    document.body.style.transition = 'opacity 0.3s ease-in-out';
    document.body.style.opacity = '0.7';
    
    await new Promise(resolve => setTimeout(resolve, 150));
    await switchMode(mode);
    
    setTimeout(() => {
      document.body.style.opacity = '1';
      setTimeout(() => {
        document.body.style.transition = '';
      }, 300);
    }, 150);
  }, [switchMode]);

  const updateModePreferences = useCallback(async (mode: AppMode, preferences: Partial<ModePreferences[AppMode]>) => {
    const updatedPreferences = {
      ...settings.modePreferences,
      [mode]: {
        ...settings.modePreferences[mode],
        ...preferences
      }
    };
    
    await saveSettings({ modePreferences: updatedPreferences });
  }, [settings.modePreferences, saveSettings]);

  const completeModeSetup = useCallback(async () => {
    await saveSettings({ hasCompletedModeSetup: true });
    showSuccess('Mode Setup Complete!', 'You can now switch between dating and hookup modes');
  }, [saveSettings, showSuccess]);

  const setUseMetric = useCallback(async (value: boolean) => {
    await saveSettings({ useMetric: value });
  }, [saveSettings]);

  const setAutoSwitchByTime = useCallback(async (enabled: boolean) => {
    await saveSettings({ autoSwitchByTime: enabled });
    
    if (enabled) {
      showInfo('Auto-Switch Enabled', 'App will switch modes based on time of day');
    }
  }, [saveSettings, showInfo]);

  const updateTimeBasedSwitching = useCallback(async (timeSettings: AppSettings['timeBasedSwitching']) => {
    await saveSettings({ timeBasedSwitching: timeSettings });
  }, [saveSettings]);

  const updatePrivacySettings = useCallback(async (privacySettings: Partial<AppSettings['privacySettings']>) => {
    const updated = { ...settings.privacySettings, ...privacySettings };
    await saveSettings({ privacySettings: updated });
  }, [settings.privacySettings, saveSettings]);

  // Utility functions
  const getModeDisplayName = useCallback((mode: AppMode): string => {
    switch (mode) {
      case 'dating': return 'Dating';
      case 'hookup': return 'Hookup';
      default: return 'Unknown';
    }
  }, []);

  const getModeDescription = useCallback((mode: AppMode): string => {
    switch (mode) {
      case 'dating': return 'Looking for meaningful connections and relationships';
      case 'hookup': return 'Looking for casual encounters and fun';
      default: return '';
    }
  }, []);

  const getModeIcon = useCallback((mode: AppMode): string => {
    switch (mode) {
      case 'dating': return '💕';
      case 'hookup': return '🔥';
      default: return '❓';
    }
  }, []);

  const logModeSwitch = useCallback((fromMode: AppMode, toMode: AppMode, method: 'manual' | 'auto' | 'scheduled') => {
    // Log to analytics service
    console.log(`Mode switch: ${fromMode} → ${toMode} (${method})`);
    
    // In a real app, you'd send this to your analytics service
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'mode_switch', {
        event_category: 'user_behavior',
        event_label: `${fromMode}_to_${toMode}`,
        custom_parameter_1: method
      });
    }
  }, []);

  const contextValue: AppContextType = {
    // Loading
    isLoading,
    
    // Basic settings
    useMetric: settings.useMetric,
    setUseMetric,
    
    // Mode management
    currentMode: settings.currentMode,
    switchMode,
    switchModeWithTransition,
    
    // Mode preferences
    modePreferences: settings.modePreferences,
    updateModePreferences,
    
    // Setup
    hasCompletedModeSetup: settings.hasCompletedModeSetup,
    completeModeSetup,
    
    // Auto-switching
    autoSwitchByTime: settings.autoSwitchByTime,
    setAutoSwitchByTime,
    timeBasedSwitching: settings.timeBasedSwitching,
    updateTimeBasedSwitching,
    
    // Privacy
    privacySettings: settings.privacySettings,
    updatePrivacySettings,
    
    // Utilities
    getModeDisplayName,
    getModeDescription,
    getModeIcon,
    
    // Analytics
    logModeSwitch
  };

  return (
    <EnhancedAppContext.Provider value={contextValue}>
      {children}
    </EnhancedAppContext.Provider>
  );
};

export default EnhancedAppContext;